import type { Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import type {
  CreateSupplierIncomingTemplateInput,
  CreateSupplierIncomingResultInput,
  SupplierIncomingResult,
  SupplierIncomingResultFilters,
  SupplierIncomingTemplate,
  SupplierIncomingTemplateWithRelations,
  SupplierIncomingTestResult,
} from './types';

type SupplierIncomingResultRow = Omit<SupplierIncomingResult, 'test_results'> & {
  test_results: Json;
};

function normalizeTestResult(raw: Record<string, unknown>): SupplierIncomingTestResult {
  return {
    name: typeof raw.name === 'string' ? raw.name : '',
    value: typeof raw.value === 'string' ? raw.value : '',
    numeric_value: typeof raw.numeric_value === 'number' ? raw.numeric_value : null,
    unit: typeof raw.unit === 'string' ? raw.unit : '',
    specification: typeof raw.specification === 'string' ? raw.specification : '',
    tolerance: typeof raw.tolerance === 'string' ? raw.tolerance : '',
    ucl: typeof raw.ucl === 'number' ? raw.ucl : null,
    lcl: typeof raw.lcl === 'number' ? raw.lcl : null,
    passed: typeof raw.passed === 'boolean' ? raw.passed : null,
  };
}

function normalizeTestResults(raw: Json): SupplierIncomingTestResult[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const results: SupplierIncomingTestResult[] = [];

  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      continue;
    }

    results.push(normalizeTestResult(entry as Record<string, unknown>));
  }

  return results;
}

function mapResultRow(row: SupplierIncomingResultRow): SupplierIncomingResult {
  return {
    ...row,
    test_results: normalizeTestResults(row.test_results),
  };
}

async function attachAssignedSuppliers(
  tenantId: string,
  factoryId: string,
  templates: SupplierIncomingTemplate[],
): Promise<SupplierIncomingTemplateWithRelations[]> {
  const supplierIds = [...new Set(templates.map((template) => template.assigned_supplier_id).filter(Boolean))];

  if (supplierIds.length === 0) {
    return templates;
  }

  const { data: suppliers, error } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .select('id, name, code')
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .in('id', supplierIds);

  if (error) {
    throw error;
  }

  const supplierById = new Map((suppliers ?? []).map((supplier) => [supplier.id, supplier]));

  return templates.map((template) => ({
    ...template,
    assigned_supplier: template.assigned_supplier_id
      ? supplierById.get(template.assigned_supplier_id) ?? null
      : null,
  }));
}

export async function fetchSupplierIncomingTemplatesByFactory(
  tenantId: string,
  factoryId: string,
): Promise<SupplierIncomingTemplateWithRelations[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.schema('core') as any)
    .from('mod_supplier_incoming_templates')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  const templates = (data ?? []) as SupplierIncomingTemplate[];
  return attachAssignedSuppliers(tenantId, factoryId, templates);
}

export async function createSupplierIncomingTemplate(
  input: CreateSupplierIncomingTemplateInput,
): Promise<SupplierIncomingTemplateWithRelations> {
  const selfServiceEnabled = input.supplier_self_service ?? false;
  const supplierMagicToken =
    input.supplier_magic_token === undefined
      ? selfServiceEnabled
        ? crypto.randomUUID()
        : null
      : input.supplier_magic_token;
  const supplierMagicTokenExpiresAt =
    input.supplier_magic_token_expires_at === undefined
      ? selfServiceEnabled
        ? new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
        : null
      : input.supplier_magic_token_expires_at;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.schema('core') as any)
    .from('mod_supplier_incoming_templates')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      name: input.name,
      description: input.description ?? null,
      is_active: input.is_active ?? true,
      area_id: input.area_id ?? null,
      line_id: input.line_id ?? null,
      station_id: input.station_id ?? null,
      supplier_self_service: selfServiceEnabled,
      assigned_supplier_id: input.assigned_supplier_id ?? null,
      supplier_magic_token: supplierMagicToken,
      supplier_magic_token_expires_at: supplierMagicTokenExpiresAt,
      created_by: input.created_by ?? null,
    })
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  const template = data as unknown as SupplierIncomingTemplate;
  if (!template.assigned_supplier_id) {
    return template;
  }

  const { data: supplier, error: supplierError } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .select('id, name, code')
    .eq('tenant_id', template.tenant_id)
    .eq('factory_id', template.factory_id)
    .eq('id', template.assigned_supplier_id)
    .maybeSingle();

  if (supplierError) {
    throw supplierError;
  }

  return {
    ...template,
    assigned_supplier: supplier ?? null,
  };
}

export async function fetchSupplierIncomingTemplateByToken(
  token: string,
): Promise<SupplierIncomingTemplateWithRelations | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.schema('core') as any)
    .from('mod_supplier_incoming_templates')
    .select('*')
    .eq('supplier_magic_token', token)
    .eq('supplier_self_service', true)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  const rawTemplate: unknown = data;
  const template = rawTemplate as SupplierIncomingTemplate;
  if (
    template.supplier_magic_token_expires_at &&
    new Date(template.supplier_magic_token_expires_at) < new Date()
  ) {
    return null;
  }

  if (!template.assigned_supplier_id) {
    return template;
  }

  const { data: supplier, error: supplierError } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .select('id, name, code')
    .eq('tenant_id', template.tenant_id)
    .eq('factory_id', template.factory_id)
    .eq('id', template.assigned_supplier_id)
    .maybeSingle();

  if (supplierError) {
    throw supplierError;
  }

  return {
    ...template,
    assigned_supplier: supplier ?? null,
  };
}

export async function fetchSupplierIncomingResultsByFactory(
  tenantId: string,
  factoryId: string,
  filters: SupplierIncomingResultFilters = {},
): Promise<SupplierIncomingResult[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query: any = (supabase.schema('core') as any)
    .from('mod_supplier_incoming_results')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (filters.sinceIso) {
    query = query.gte('created_at', filters.sinceIso);
  }

  if (filters.supplierId) {
    query = query.eq('supplier_id', filters.supplierId);
  }

  if (filters.limit && filters.limit > 0) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  return ((data ?? []) as SupplierIncomingResultRow[]).map(mapResultRow);
}

export async function fetchSupplierIncomingResultsBySupplier(
  tenantId: string,
  factoryId: string,
  supplierId: string,
  limit = 10,
): Promise<SupplierIncomingResult[]> {
  return fetchSupplierIncomingResultsByFactory(tenantId, factoryId, {
    supplierId,
    limit,
  });
}

export async function getSupplierIncomingDocumentSignedUrl(
  filePath: string,
  expiresIn = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('incoming-inspection-docs')
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

export async function createSupplierIncomingResult(
  input: CreateSupplierIncomingResultInput,
): Promise<SupplierIncomingResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.schema('core') as any)
    .from('mod_supplier_incoming_results')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      template_id: input.template_id,
      submitted_by: input.submitted_by ?? null,
      document_type: input.document_type ?? 'coa',
      document_file_path: input.document_file_path,
      document_file_name: input.document_file_name,
      material_name: input.material_name ?? null,
      batch_lot_number: input.batch_lot_number ?? null,
      supplier_name: input.supplier_name ?? null,
      supplier_id: input.supplier_id ?? null,
      material_id: input.material_id ?? null,
      document_date: input.document_date ?? null,
      expiry_date: input.expiry_date ?? null,
      test_results: input.test_results ?? [],
      raw_ocr_response: input.raw_ocr_response ?? null,
      overall_status: input.overall_status ?? 'pending',
      inspector_notes: input.inspector_notes ?? null,
    })
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  const rawRow: unknown = data;
  return mapResultRow(rawRow as SupplierIncomingResultRow);
}
